import { CheckCircle2, Clock, Search, Users } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { formatTime } from '@/lib/utils'
import type { PlannedMeal } from '@/types/meal-plan'
import type { Recipe } from '@/types/recipe'
import type { DayPlan } from './meal-planning-utils'
import { formatDutchDate } from './meal-planning-utils'

interface RecipePickerProps {
  selectedDay?: DayPlan
  selectedDayMeals: PlannedMeal[]
  suggestedRecipes: Recipe[]
  recipeSearch: string
  onSearchChange: (value: string) => void
  onPlanRecipe: (recipeId: string) => Promise<void>
  loadingPlannedMeals: boolean
  replacingDateKey: string | null
}

export function RecipePicker({
  selectedDay,
  selectedDayMeals,
  suggestedRecipes,
  recipeSearch,
  onSearchChange,
  onPlanRecipe,
  loadingPlannedMeals,
  replacingDateKey,
}: RecipePickerProps) {
  return (
    <Card className="xl:sticky xl:top-24 xl:max-h-[calc(100vh-6rem)] xl:overflow-hidden">
      <CardHeader className="space-y-4">
        <div>
          <CardTitle>Plan voor {selectedDay?.label.toLowerCase()}</CardTitle>
          <p className="mt-1 text-sm text-gray-600">
            {selectedDay ? formatDutchDate(selectedDay.date, { weekday: 'long', day: 'numeric', month: 'long' }) : ''}
          </p>
        </div>

        <div className="rounded-lg border border-orange-200 bg-orange-50 p-3 text-sm text-orange-800">
          Een gekozen recept vervangt het huidige dagmenu voor deze dag. Zo blijft de weekplanning overzichtelijk.
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            value={recipeSearch}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Zoek recept, ingrediënt of tag..."
            className="pl-9"
          />
        </div>
      </CardHeader>

      <CardContent className="space-y-3 xl:max-h-[calc(100vh-20rem)] xl:overflow-y-auto">
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
          {selectedDayMeals.length ? (
            <>
              Huidig dagmenu:
              <div className="mt-2 space-y-1">
                {selectedDayMeals.map((meal) => (
                  <div key={meal.id} className="break-words font-medium text-gray-900">
                    {meal.recipe?.title ?? 'Onbekend recept'}
                  </div>
                ))}
              </div>
            </>
          ) : (
            'Kies hieronder een recept om deze dag te vullen.'
          )}
        </div>

        {suggestedRecipes.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 p-6 text-center text-sm text-gray-600">
            Geen recepten gevonden voor deze zoekopdracht.
          </div>
        ) : (
          suggestedRecipes.map((recipe) => {
            const alreadyPlanned = selectedDayMeals.some((meal) => meal.recipe_id === recipe.id)

            return (
              <div key={recipe.id} className="rounded-lg border border-gray-200 bg-white p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="break-words font-medium text-gray-900">{recipe.title}</div>
                    {recipe.description ? <div className="mt-1 text-sm text-gray-600">{recipe.description}</div> : null}
                    <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-gray-500">
                      {recipe.prep_time || recipe.cook_time ? (
                        <span className="flex items-center gap-1">
                          <Clock className="h-3.5 w-3.5" />
                          {formatTime((recipe.prep_time || 0) + (recipe.cook_time || 0))}
                        </span>
                      ) : null}
                      {recipe.servings ? (
                        <span className="flex items-center gap-1">
                          <Users className="h-3.5 w-3.5" />
                          {recipe.servings}
                        </span>
                      ) : null}
                    </div>
                  </div>

                  <Button
                    onClick={() => void onPlanRecipe(recipe.id)}
                    disabled={loadingPlannedMeals && replacingDateKey === selectedDay?.dateKey}
                  >
                    {selectedDayMeals.length ? 'Vervangen' : 'Plannen'}
                  </Button>
                </div>

                {alreadyPlanned ? (
                  <div className="mt-3 flex items-center gap-2 text-xs text-green-700">
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    Dit recept staat al op deze dag.
                  </div>
                ) : null}
              </div>
            )
          })
        )}
      </CardContent>
    </Card>
  )
}
